import React from 'react';
import { createBrowserRouter, createRoutesFromElements, RouterProvider, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import NewVersionBanner from './components/NewVersionBanner';
import CssBaseline from '@mui/material/CssBaseline';
import Layout from './components/layout/Layout';
import FlowBuilderPage from './pages/FlowBuilderPage';
import FlowsPage from './pages/flows/FlowsPage';
import FlowApiSpecPage from './pages/flows/FlowApiSpecPage';
import AttributionPage from './pages/AttributionPage';
import LeadsPage from './pages/LeadsPage';
import SubmissionsPage from './pages/SubmissionsPage';
import BookingsPage from './pages/BookingsPage';
import SettingsPage from './pages/settings/SettingsPage';
import LoginPage from './pages/auth/LoginPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import OAuthCallback from './pages/auth/OAuthCallback';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AccountOnlyRoute from './components/auth/AccountOnlyRoute';
import DemoRoutes from './components/auth/DemoRoutes';
import ErrorBoundary from './components/common/ErrorBoundary';
import { usePreferences } from './contexts/PreferencesContext';
import { AD_ATTRIBUTION_ENABLED } from './features';

// FlowGate app chrome: calm neutral grays with indigo as the SINGLE accent.
// The brand gradient lives only in the logo mark; teal/amber stay out of the
// app chrome. Color is reserved for meaning: red = errors, green = the
// WhatsApp phone previews (customer-facing content, not our UI).
const theme = createTheme({
  palette: {
    primary: {
      main: '#4F46E5', // indigo, the one accent
      dark: '#4338CA',
    },
    secondary: {
      main: '#697079', // neutral gray, no second accent in the chrome
      dark: '#3D434E',
    },
    text: {
      primary: '#0B0D12', // ink
      secondary: '#3D434E',
    },
    divider: '#E6E8EC',
    background: {
      default: '#F5F6F8',
    },
  },
  typography: {
    fontFamily: '"Geist", "Inter", ui-sans-serif, system-ui, sans-serif',
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        colorDefault: {
          backgroundColor: '#fff',
          color: '#0B0D12',
          boxShadow: 'none',
          borderBottom: '1px solid #E6E8EC',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          boxShadow: '0 6px 16px -8px rgba(79,70,229,.6)',
          '&:hover': {
            backgroundColor: '#4338CA',
            boxShadow: '0 6px 16px -8px rgba(79,70,229,.6)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 2px rgba(11,13,18,.04), 0 8px 24px -18px rgba(11,13,18,.1)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        primary: {
          boxShadow: '0 12px 28px -10px rgba(79,70,229,.65)',
          '&:hover': {
            backgroundColor: '#4338CA',
          },
        },
      },
    },
  },
});

// Gate the CTWA attribution pages: if the feature is off, send the user back to /flows (keeps
// the URL honest with the hidden nav). Two gates, and the build-time one is checked first, a
// bookmarked /leads must not open a page the nav says does not exist, and asking the server for
// preferences to decide that is a load and a flash for a question already answered.
function AdAttributionRoute({ children }) {
  const { preferences, loading } = usePreferences();
  if (!AD_ATTRIBUTION_ENABLED) return <Navigate to="/flows" replace />;
  if (loading) return null;
  if (!preferences.adAttributionEnabled) return <Navigate to="/flows" replace />;
  return children;
}

// A data router rather than <BrowserRouter> (same routes): the modern router API, and what a
// page would need to block in-app navigation, should that ever be wanted again.
const router = createBrowserRouter(createRoutesFromElements(
  <>
    {/* Public routes */}
    <Route path="/login" element={<LoginPage />} />
    <Route path="/auth/callback" element={<OAuthCallback />} />

    {/* An invitation link. Outside the shell and outside ProtectedRoute: the person opening it
        may have no account yet, and the page's whole job is to say what the link is for before
        asking them to sign in. */}
    <Route path="/invite/:token" element={<AcceptInvitePage />} />

    {/* Protected routes with layout */}
    <Route path="/*" element={
      <ProtectedRoute>
        <Layout>
          <ErrorBoundary label="This page hit an error">
            {/* A demo account gets the builder and nothing else, see DemoRoutes. */}
            <DemoRoutes builder={<FlowBuilderPage />}>
            <Routes>
              <Route path="/" element={<FlowsPage />} />
              <Route path="/flow-builder" element={<FlowBuilderPage />} />
              <Route path="/flow-builder/:flowId" element={<FlowBuilderPage />} />
              <Route path="/flows" element={<FlowsPage />} />
              {/* A flow's live-data API, generated from the flow each time it is opened. */}
              <Route path="/flows/:flowId/api" element={<FlowApiSpecPage />} />
              <Route path="/submissions" element={<AccountOnlyRoute><SubmissionsPage /></AccountOnlyRoute>} />
              {/* Renamed 2026-09-20. "Enquiry" reads as a sales lead; the list holds every
                  completed form, which is what the backend has called a submission all along.
                  The old path is bookmarked and pasted into notes, so it keeps working. */}
              <Route path="/enquiries" element={<Navigate to="/submissions" replace />} />
              <Route path="/bookings" element={<BookingsPage />} />
              <Route path="/leads" element={<AdAttributionRoute><LeadsPage /></AdAttributionRoute>} />
              <Route path="/attribution" element={<AdAttributionRoute><AttributionPage /></AdAttributionRoute>} />
              <Route path="/settings" element={<SettingsPage />} />
            </Routes>
            </DemoRoutes>
          </ErrorBoundary>
        </Layout>
      </ProtectedRoute>
    } />
  </>
));

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NewVersionBanner />
      <RouterProvider router={router} />
    </ThemeProvider>
  );
}

export default App;
